import { Button, Card, makeStyles, Text, tokens } from '@fluentui/react-components';
import { Dismiss20Regular } from '@fluentui/react-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../../libs/hooks/useChat';
import { useAppDispatch } from '../../../redux/app/hooks';
import { markMessageCsatSubmitted } from '../../../redux/features/conversations/conversationsSlice';

const useStyles = makeStyles({
    card: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderLeft: `4px solid ${tokens.colorBrandStroke1}`,
        padding: tokens.spacingVerticalM,
        marginTop: tokens.spacingVerticalS,
        position: 'relative',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalS,
    },
    title: {
        fontWeight: tokens.fontWeightSemibold,
    },
    emojiRow: {
        display: 'flex',
        justifyContent: 'space-around',
        gap: tokens.spacingHorizontalS,
    },
    emojiButton: {
        fontSize: '24px',
        minWidth: '44px',
        height: '44px',
        padding: 0,
        borderRadius: '50%',
        lineHeight: 1,
    },
    thankYou: {
        textAlign: 'center',
        color: tokens.colorNeutralForeground2,
    },
    dismiss: {
        minWidth: '24px',
        height: '24px',
        padding: 0,
    },
});

const EMOJIS: Array<{ rating: number; emoji: string; labelKey: string }> = [
    { rating: 1, emoji: '😠', labelKey: 'csat.emojiTerrible' },
    { rating: 2, emoji: '🙁', labelKey: 'csat.emojiBad' },
    { rating: 3, emoji: '😐', labelKey: 'csat.emojiOk' },
    { rating: 4, emoji: '🙂', labelKey: 'csat.emojiGreat' },
    { rating: 5, emoji: '🤩', labelKey: 'csat.emojiAmazing' },
];

interface InlineCsatWidgetProps {
    chatId: string;
    messageId: string;
    // The persisted/broadcast "user already responded" flag for this message (#1767). When it's
    // set but this tab wasn't the one that acted, the widget collapses immediately — another tab
    // answered, or this is a reload of an already-answered chat.
    answered: boolean;
}

export const InlineCsatWidget: React.FC<InlineCsatWidgetProps> = ({ chatId, messageId, answered }) => {
    const styles = useStyles();
    const { t } = useTranslation();
    const chat = useChat();
    const dispatch = useAppDispatch();
    const [submitted, setSubmitted] = useState(false);
    const [picked, setPicked] = useState<number | null>(null);
    // True once this tab has run its own submit/dismiss flow and collapsed the widget.
    const [hidden, setHidden] = useState(false);
    // Set synchronously the moment the user acts in THIS tab — before the await — so the
    // csatSubmitted echo the server broadcasts back to us (#1767) can't pre-empt our own
    // "thank you" beat. The `answered && !acted` collapse below only fires for tabs that didn't act.
    const [acted, setActed] = useState(false);
    const collapseTimerRef = useRef<number | undefined>(undefined);

    // Clear a pending thank-you-beat collapse if the widget unmounts (e.g. chat switch) before
    // the 1200 ms fires. The persisted CsatSubmitted flag still collapses it on return/reload.
    useEffect(
        () => () => {
            window.clearTimeout(collapseTimerRef.current);
        },
        [],
    );

    const handlePick = async (rating: number) => {
        if (submitted) return;
        setActed(true);
        setPicked(rating);
        const ok = await chat.submitCsatRatings(rating, undefined, 'inline');
        if (!ok) {
            // submitCsatRatings already surfaced an error alert. Keep the widget open and clear
            // the highlight so the user can retry instead of silently losing the rating.
            setActed(false);
            setPicked(null);
            return;
        }
        setSubmitted(true);
        // Brief delay so the user sees the highlight before the collapse.
        collapseTimerRef.current = window.setTimeout(() => {
            dispatch(markMessageCsatSubmitted({ chatId, messageId }));
            setHidden(true);
        }, 1200);
    };

    const handleDismiss = async () => {
        setActed(true);
        try {
            await chat.declineCsat();
        } catch {
            // declineCsat swallows its own errors today, but if it ever rethrows, a failed
            // dismiss must not leave `acted` stuck — that would block the `answered && !acted`
            // collapse from peer-tab broadcasts. Keep the widget open for retry instead.
            setActed(false);
            return;
        }
        dispatch(markMessageCsatSubmitted({ chatId, messageId }));
        setHidden(true);
    };

    // Collapse once this tab finished its own flow (hidden), or the chat was answered elsewhere —
    // another open tab or a reload after a previous response. The `!acted` guard keeps the acting
    // tab's thank-you beat intact even though the server echoes csatSubmitted back to it too.
    if (hidden || (answered && !acted)) {
        return null;
    }

    if (submitted) {
        return (
            <Card className={styles.card} aria-live="polite">
                <Text className={styles.thankYou}>{t('csat.inlineThankYou')}</Text>
            </Card>
        );
    }

    return (
        <Card className={styles.card} data-testid="inline-csat-widget">
            <div className={styles.header}>
                <Text className={styles.title}>{t('csat.inlineTitle')}</Text>
                <Button
                    appearance="transparent"
                    icon={<Dismiss20Regular />}
                    className={styles.dismiss}
                    aria-label={t('csat.dismiss')}
                    onClick={() => {
                        void handleDismiss();
                    }}
                    data-testid="inline-csat-dismiss"
                />
            </div>
            <div className={styles.emojiRow}>
                {EMOJIS.map(({ rating, emoji, labelKey }) => (
                    <Button
                        key={rating}
                        appearance={picked === rating ? 'primary' : 'transparent'}
                        className={styles.emojiButton}
                        aria-label={t(labelKey)}
                        title={t(labelKey)}
                        data-testid={`inline-csat-emoji-${rating.toString()}`}
                        onClick={() => {
                            void handlePick(rating);
                        }}
                    >
                        {emoji}
                    </Button>
                ))}
            </div>
        </Card>
    );
};
